import { KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ServiceCompanySearch, {
  CompanySearchOutcome,
  CompanySearchRow,
} from '../../services/service-company-search';
import CompanySearchView from './CompanySearchView';
import { useDebouncedState } from '../../util/hooks';
import styles from './app-company-search.module.scss';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const RETRY_DELAY_MS = 1000;

type Search = (query: string) => Promise<CompanySearchOutcome>;
type Typing = { text: string; edit: number };
type Answer = { query: string; edit: number; outcome: CompanySearchOutcome };

export type SearchDown = 'off' | 'unavailable';
export type OnUnavailable = (kind: SearchDown, hadFocus: boolean) => void;

type Props = {
  value: CompanySearchRow | null;
  onChange: (company: CompanySearchRow | null) => void;
  onUnavailable: OnUnavailable;
  label?: ReactNode;
  required?: boolean;
  errorMessage?: string;
  prefix?: ReactNode;
  selectedAction?: ReactNode;
  search?: Search;
};

const searchRegister: Search = query => ServiceCompanySearch.suggest(query);

async function askSafely(search: Search, query: string): Promise<CompanySearchOutcome> {
  try {
    return await search(query);
  } catch {
    return { kind: 'unavailable' };
  }
}

const pause = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function askWithRetry(
  search: Search,
  query: string,
  stillWanted: () => boolean,
): Promise<CompanySearchOutcome> {
  const first = await askSafely(search, query);
  if (first.kind !== 'unavailable') return first;
  await pause(RETRY_DELAY_MS);
  return stillWanted() ? askSafely(search, query) : first;
}

export default function CompanySearch({
  value,
  onChange,
  onUnavailable,
  label,
  required,
  errorMessage,
  prefix,
  selectedAction,
  search = searchRegister,
}: Props) {
  const [t] = useTranslation('companySearch');
  const inputId = useId();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const changeRef = useRef<HTMLDivElement>(null);
  const focusNext = useRef<'input' | 'change' | null>(null);
  // Read through a ref so a caller's inline callback cannot restart the searches.
  const reportUnavailable = useRef(onUnavailable);
  reportUnavailable.current = onUnavailable;

  const [typing, setTyping, settledTyping] = useDebouncedState<Typing>(
    { text: '', edit: 0 },
    DEBOUNCE_MS,
  );
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const picked = value !== null;
  const asked = settledTyping.text.trim();
  const askedEdit = settledTyping.edit;
  const hasFocus = () => document.activeElement === inputRef.current;

  // An empty query costs the register nothing, and the proxy still answers 503 when search is off.
  useEffect(() => {
    let current = true;
    askSafely(search, '').then(outcome => {
      if (current && outcome.kind === 'off') reportUnavailable.current('off', hasFocus());
    });
    return () => {
      current = false;
    };
  }, [search]);

  // Depending on the pick drops an answer still in flight, so a late failure cannot replace the card.
  useEffect(() => {
    if (picked || asked.length < MIN_QUERY_LENGTH) return undefined;
    const typedSinceBusy = answer?.outcome.kind === 'busy' && answer.edit !== askedEdit;
    if (answer?.query === asked && !typedSinceBusy) return undefined;
    let current = true;
    askWithRetry(search, asked, () => current).then(outcome => {
      if (!current) return;
      if (outcome.kind === 'off' || outcome.kind === 'unavailable') {
        reportUnavailable.current(outcome.kind, hasFocus());
      }
      setAnswer({ query: asked, edit: askedEdit, outcome });
      setActiveIndex(0);
    });
    return () => {
      current = false;
    };
  }, [answer, asked, askedEdit, picked, search]);

  // A pick and a change of pick each unmount the focused element.
  useEffect(() => {
    const target = focusNext.current;
    focusNext.current = null;
    if (target === 'input') inputRef.current?.focus();
    if (target === 'change') changeRef.current?.focus();
  }, [value]);

  const typed = typing.text.trim();
  const outcome = answer?.query === typed ? answer.outcome : undefined;
  const found = outcome?.kind === 'found' ? outcome : null;
  const rows = found?.rows ?? [];
  const rateLimited = outcome?.kind === 'busy';
  const loading = typed.length >= MIN_QUERY_LENGTH && !outcome;
  const listOpen = open && rows.length > 0;

  const onQueryChange = (text: string) => {
    setTyping({ text, edit: typing.edit + 1 });
    setOpen(true);
  };

  const pick = (company: CompanySearchRow) => {
    setOpen(false);
    focusNext.current = 'change';
    onChange(company);
  };

  const changePick = () => {
    focusNext.current = 'input';
    onChange(null);
  };

  const clear = () => {
    setTyping({ text: '', edit: typing.edit + 1 });
    inputRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && rows.length > 0) {
      event.preventDefault();
      if (open) setActiveIndex(index => Math.min(rows.length - 1, index + 1));
      else setOpen(true);
    } else if (event.key === 'ArrowUp' && listOpen) {
      event.preventDefault();
      setActiveIndex(index => Math.max(0, index - 1));
    } else if (event.key === 'Enter' && listOpen) {
      event.preventDefault();
      pick(rows[activeIndex]);
    } else if (event.key === 'Escape' && open) {
      // Stopped here so a surrounding modal only closes on a second Escape.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  };

  const announcement = () => {
    if (rateLimited) {
      return t(
        'status.rateLimited',
        'För många sökningar just nu. Vänta en stund och försök igen.',
      );
    }
    if (value || !open || !found) return '';
    if (rows.length === 0) {
      return t('noMatch.title', 'Inget företag matchar det namnet eller numret.');
    }
    const count = t('status.found', '{{count}} företag hittades.', { count: rows.length });
    if (!found.truncated) return count;
    return `${count} ${t('status.keepTyping', 'Fortsätt skriva för att förfina listan.')}`;
  };

  return (
    <>
      <CompanySearchView
        inputId={inputId}
        listId={listId}
        inputRef={inputRef}
        changeRef={changeRef}
        label={label}
        required={required}
        errorMessage={errorMessage}
        prefix={prefix}
        selectedAction={selectedAction}
        query={typing.text}
        loading={loading}
        rateLimited={rateLimited}
        found={found}
        open={open}
        activeIndex={activeIndex}
        selected={value}
        onQueryChange={onQueryChange}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onPick={pick}
        onClear={clear}
        onChangePick={changePick}
      />
      <div role="status" className={styles.srOnly}>
        {announcement()}
      </div>
    </>
  );
}
